import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { createRequest } from '../../utils/helpers/api_utils';
import { apiUrl } from '../../utils/api_service/api_url';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { clearPending, setPending } from '../../utils/auth/mfaHandoff';

/**
 * Landing page after the IdP round-trip. The backend redirects here with a
 * one-time code (never a token) which is exchanged for either a session or,
 * when the user still owes a second factor, the MFA challenge / enrolment
 * token — handed to the MFA pages the same way password login does.
 */
export const SsoCallback = () => {
  const history = useHistory();
  const [error, setError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const deepLink = params.get('next') || '';
    if (!code) {
      setError(true);
      return;
    }
    fetch(
      createRequest(`${apiUrl}/auth/sso/exchange`, 'POST', 'application/json', false, {
        code,
      }),
    )
      .then((r) => r.json())
      .then(async (d) => {
        if (!d.success) {
          setError(true);
          return;
        }
        if (d.mfaRequired && d.challengeToken) {
          setPending('challenge', d.challengeToken, deepLink);
          history.replace('/auth/mfa');
          return;
        }
        if (d.mfaEnrollmentRequired && d.enrollToken) {
          setPending('enroll', d.enrollToken, deepLink);
          history.replace('/auth/mfa/enroll');
          return;
        }
        if (!d.accessToken) {
          setError(true);
          return;
        }
        clearPending('challenge');
        clearPending('enroll');
        // One seam for "signed in": SSO ends in the same state as password login.
        history.replace(await finishSignIn(d, { deepLink }));
      })
      .catch(() => setError(true));
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8fafc',
        p: 2,
      }}
    >
      <Paper
        variant='outlined'
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
          borderColor: 'divider',
        }}
      >
        {error ? (
          <Box>
            <Typography variant='h6' fontWeight={700} color='text.primary' sx={{ mb: 1 }}>
              Sign-in Error
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              We could not complete your sign-in.
            </Typography>
            <Button
              variant='contained'
              onClick={() => history.push('/login')}
              sx={{
                bgcolor: '#10b981',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
              }}
            >
              Back to sign in
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} sx={{ color: '#10b981' }} />
            <Typography variant='body2' color='text.secondary'>
              Signing you in…
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
